import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  Divider,
  Chip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  AdminPanelSettings,
  Person,
  Visibility,
  VisibilityOff,
  BusinessCenter,
  AccountCircle,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [contractorEmail, setContractorEmail] = useState('');
  const [contractorPassword, setContractorPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showContractorPassword, setShowContractorPassword] = useState(false);
  const [activeForm, setActiveForm] = useState<'ADMIN' | 'CONTRACTOR' | null>(null);
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      // User is already authenticated, redirect to appropriate dashboard
      const userRole = user.role?.toUpperCase();
      console.log('User already logged in, redirecting based on role:', userRole);
      
      if (userRole === 'CONTRACTOR') {
        navigate('/contractor/dashboard', { replace: true });
      } else if (userRole === 'ADMIN') {
        navigate('/dashboard', { replace: true });
      } else {
        // Unknown role, stay on login page
        console.warn('Unknown user role, staying on login page');
      }
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent, expectedRole: 'ADMIN' | 'CONTRACTOR') => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setActiveForm(expectedRole);

    const email = expectedRole === 'ADMIN' ? adminEmail : contractorEmail;
    const password = expectedRole === 'ADMIN' ? adminPassword : contractorPassword;

    if (!email || !password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    try {
      const result = await login(email, password);
      const userRole = result.user.role?.toUpperCase(); // Normalize to uppercase
      const normalizedExpectedRole = expectedRole.toUpperCase();

      // Debug logging
      console.log('Login result:', { userRole, expectedRole: normalizedExpectedRole, result });

      // Role validation - prevent role mixing errors
      if (userRole !== normalizedExpectedRole) {
        const roleName = expectedRole === 'ADMIN' ? 'Administrator' : 'Contractor';
        const actualRoleName = userRole === 'ADMIN' ? 'Administrator' : 'Contractor';
        setError(`This account is registered as ${actualRoleName}, not ${roleName}. Please use the correct login section.`);
        setLoading(false);
        return;
      }

      // Automatic role-based redirection with explicit paths
      // CRITICAL: Force redirect based on role immediately after login
      // Don't rely on any other navigation logic
      console.log('[Login] Login successful, redirecting based on role:', userRole);
      
      if (userRole === 'CONTRACTOR') {
        // ALWAYS redirect contractors to contractor portal - IMMEDIATE
        console.log('[Login] Contractor detected - redirecting to /contractor/dashboard');
        // Use setTimeout to ensure state is fully updated before navigation
        setTimeout(() => {
          navigate('/contractor/dashboard', { replace: true });
        }, 100);
      } else if (userRole === 'ADMIN') {
        // ALWAYS redirect admins to Dashboard - IMMEDIATE
        console.log('[Login] Admin detected - redirecting to /dashboard');
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 100);
      } else {
        // Fallback for unknown roles
        console.error('[Login] Unknown user role:', userRole);
        setError('Unknown user role. Please contact support.');
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Login failed';
      setError(errorMessage);
      setLoading(false);
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #FFF5F0 0%, #E3F2FD 100%)',
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Don't render login if user is already authenticated (will be redirected)
  if (user) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #FFF5F0 0%, #E3F2FD 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            component="h1"
            variant="h3"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #FF6B35 0%, #1976d2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            CRM ICV Group Pay
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
            Sign in to access your portal
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {/* Administrator Login Section */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={8}
              sx={{
                p: 4,
                height: '100%',
                background: 'linear-gradient(135deg, #FFF5F0 0%, #ffffff 100%)',
                border: '2px solid #FF6B35',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 5,
                  background: 'linear-gradient(90deg, #FF6B35 0%, #F97316 100%)',
                },
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #FF6B35 0%, #F97316 100%)',
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
                  }}
                >
                  <AdminPanelSettings sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: '#FF6B35',
                    mb: 1,
                  }}
                >
                  Administrator Portal
                </Typography>
                <Chip
                  label="ADMIN ONLY"
                  size="small"
                  sx={{
                    backgroundColor: '#FF6B35',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                />
                <Alert severity="warning" sx={{ mt: 2, mb: 2, fontSize: '0.75rem' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Administrators Only
                  </Typography>
                  <Typography variant="caption" display="block">
                    This portal is restricted to system administrators. Contractors must use the Contractor Portal on the right.
                  </Typography>
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Manage contractors, projects, billing, and system administration
                </Typography>
              </Box>

              {error && activeForm === 'ADMIN' && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Box
                component="form"
                onSubmit={(e) => handleSubmit(e, 'ADMIN')}
                sx={{ mt: 2 }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="admin-email"
                  label="Administrator Email"
                  name="email"
                  autoComplete="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessCenter sx={{ color: '#FF6B35' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#FF6B35',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#FF6B35',
                      },
                    },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="admin-password"
                  label="Password"
                  type={showAdminPassword ? 'text' : 'password'}
                  id="admin-password"
                  autoComplete="current-password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountCircle sx={{ color: '#FF6B35' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowAdminPassword(!showAdminPassword)}
                          edge="end"
                          sx={{ color: '#FF6B35' }}
                          disabled={loading}
                        >
                          {showAdminPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#FF6B35',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#FF6B35',
                      },
                    },
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    background: 'linear-gradient(135deg, #FF6B35 0%, #F97316 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #E64A19 0%, #FF6B35 100%)',
                      boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4)',
                    },
                    fontWeight: 600,
                  }}
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign In as Administrator'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Divider */}
          <Grid item xs={12} md={1} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
            <Divider orientation="vertical" sx={{ height: '100%', borderWidth: 2, borderColor: '#E0E0E0' }} />
            <Box
              sx={{
                position: 'absolute',
                backgroundColor: 'white',
                px: 2,
                py: 1,
                borderRadius: '50%',
                border: '2px solid #E0E0E0',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                OR
              </Typography>
            </Box>
          </Grid>

          {/* Contractor Login Section */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={8}
              sx={{
                p: 4,
                height: '100%',
                background: 'linear-gradient(135deg, #E3F2FD 0%, #ffffff 100%)',
                border: '2px solid #1976d2',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 5,
                  background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                },
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  }}
                >
                  <Person sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: '#1976d2',
                    mb: 1,
                  }}
                >
                  Contractor Portal
                </Typography>
                <Chip
                  label="CONTRACTOR ACCESS"
                  size="small"
                  sx={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                />
                <Alert severity="info" sx={{ mt: 2, mb: 2, fontSize: '0.75rem' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    For Contractors
                  </Typography>
                  <Typography variant="caption" display="block">
                    Use this portal to log hours, view invoices, and manage your work. Administrators must use the Administrator Portal on the left.
                  </Typography>
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Log hours, view invoices, and manage your work
                </Typography>
              </Box>

              {error && activeForm === 'CONTRACTOR' && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Box
                component="form"
                onSubmit={(e) => handleSubmit(e, 'CONTRACTOR')}
                sx={{ mt: 2 }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="contractor-email"
                  label="Contractor Email"
                  name="email"
                  autoComplete="email"
                  value={contractorEmail}
                  onChange={(e) => setContractorEmail(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: '#1976d2' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="contractor-password"
                  label="Password"
                  type={showContractorPassword ? 'text' : 'password'}
                  id="contractor-password"
                  autoComplete="current-password"
                  value={contractorPassword}
                  onChange={(e) => setContractorPassword(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountCircle sx={{ color: '#1976d2' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowContractorPassword(!showContractorPassword)}
                          edge="end"
                          sx={{ color: '#1976d2' }}
                          disabled={loading}
                        >
                          {showContractorPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                    },
                    fontWeight: 600,
                  }}
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign In as Contractor'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Help Text */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            New contractor? Check your email for the password setup link.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Having trouble? Contact your administrator for assistance.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;

